import {
  DeleteOutlined,
  PaperClipOutlined,
  PlusOutlined
} from '@ant-design/icons'
import {
  Button,
  Card,
  Checkbox,
  Col,
  Flex,
  Form,
  Input,
  Popconfirm,
  Row,
  Select,
  Upload,
  UploadFile,
  UploadProps
} from 'antd'
import { FormInstance } from 'antd/lib'
import { useEffect, useState } from 'react'

import { useMediaServiceUploadFilesEndpoint } from 'src/api/queries'
import { GetQuestionnaireResponse, QuestionType } from 'src/api/requests'
import { LANGUAGE_KEYS } from 'src/app-constants'
import {
  MAX_FILE_UPLOAD_SIZE,
  notification,
  requiredRules
} from 'src/components'
import { UploadResponse } from 'src/features/counterParts'
import { useLocales } from 'src/locales'
import i18n from 'src/locales/i18n'

import { useStyles } from '../styles'

import { CustomItemRender } from './CustomItemRender'

type Props = {
  form: FormInstance
  data?: GetQuestionnaireResponse
}

const acceptedTypesProps = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp',
  'image/tiff'
]

export const QuestionnairesQuestions = ({ data, form }: Props) => {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    {}
  )

  const { t } = useLocales()
  const { styles } = useStyles()

  const roTranslation = i18n.getFixedT('ro')(
    'questionnaires.questionnaire_title'
  )

  const ruTranslation = i18n.getFixedT('ru')(
    'questionnaires.questionnaire_title'
  )

  const roTranslationDesc = i18n.getFixedT('ro')(
    'questionnaires.questionnaire_desc'
  )

  const ruTranslationDesc = i18n.getFixedT('ru')(
    'questionnaires.questionnaire_desc'
  )

  const onAdd = async (add: () => void) => {
    const values = await form.validateFields()

    if (values) {
      add()
    }
  }

  const QUESTION_TYPES = Object.values(QuestionType).map((questionType) => ({
    label: t(`questionnaires.${questionType}`),
    value: questionType
  }))

  useEffect(() => {
    if (data && !form.getFieldValue('questions')) {
      form.setFieldsValue({
        roTitle: data?.name?.ro,
        ruTitle: data?.name?.ru,
        roDescription: data?.description?.ro,
        ruDescription: data?.description?.ru,
        questions: data?.questions
          ?.toSorted((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((question) => ({
            text: {
              ro: question?.text?.ro,
              ru: question?.text?.ru
            },
            description: {
              ro: question?.description?.ro,
              ru: question?.description?.ru
            },
            type: question?.type,
            hasOther: question?.hasOther ?? false,
            order: question?.order,
            answerChoices: question?.answerChoices
              ?.toSorted((a, b) => (a.order ?? 0) - (b.order ?? 0))
              .map((answerChoice) => ({
                text: {
                  ro: answerChoice?.text?.ro,
                  ru: answerChoice?.text?.ru
                },
                media: {
                  mediaFile: {
                    fileName: answerChoice?.mediaFile?.name,
                    uid: answerChoice?.mediaFile?.id
                  }
                },
                order: answerChoice?.order
              }))
          }))
      })
    }
  }, [data, form])

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    beforeUpload(file) {
      if (acceptedTypesProps && !acceptedTypesProps.includes(file.type)) {
        notification.error({
          message: t('errors.extension_error')
        })
        return Upload.LIST_IGNORE
      }
      if (MAX_FILE_UPLOAD_SIZE && file.size > MAX_FILE_UPLOAD_SIZE) {
        notification.error({
          message: t('errors.size_error')
        })
        return Upload.LIST_IGNORE
      }
    }
  }

  const { mutateAsync } = useMediaServiceUploadFilesEndpoint<UploadResponse>()

  const onUpload = async ({
    file,
    fieldName,
    optionName
  }: {
    file: UploadFile
    fieldName: number
    optionName: number
  }) => {
    const key = `${fieldName}-${optionName}`
    try {
      setLoadingStates((prev) => ({ ...prev, [key]: true }))

      const formData = new FormData()
      formData.append('files', file.originFileObj as Blob)

      await mutateAsync(
        {
          folder: 'questionnaire_images',
          formData: { files: [file.originFileObj] }
        },
        {
          onSuccess: async (data) => {
            const mediaFile = data?.createdResources?.[0]

            const imageUrl = URL.createObjectURL(file?.originFileObj as Blob)

            const currentAnswerChoice = form.getFieldValue([
              'questions',
              fieldName,
              'answerChoices',
              optionName
            ])

            const media = {
              mediaFile: {
                ...mediaFile,
                uid: mediaFile?.id
              },
              imageUrl
            }

            form.setFieldsValue({
              questions: {
                ...form.getFieldValue('questions'),
                [fieldName]: {
                  ...form.getFieldValue(['questions', fieldName]),
                  answerChoices: {
                    ...form.getFieldValue([
                      'questions',
                      fieldName,
                      'answerChoices'
                    ]),
                    [optionName]: {
                      ...currentAnswerChoice,
                      media,
                      mediaFileId: media?.mediaFile?.id
                    }
                  }
                }
              }
            })
          }
        }
      )
    } finally {
      setLoadingStates((prev) => ({ ...prev, [key]: false }))
    }
  }

  return (
    <Flex vertical gap={24}>
      <Card>
        <Row gutter={[24, 24]}>
          <Col span={12}>
            <Form.Item label={roTranslation} name="roTitle">
              <Input placeholder={roTranslation} suffix={<>RO</>} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={ruTranslation} name="ruTitle">
              <Input placeholder={ruTranslation} suffix={<>РУ</>} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={roTranslationDesc} name="roDescription">
              <Input.TextArea rows={3} placeholder={roTranslationDesc} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={ruTranslationDesc} name="ruDescription">
              <Input.TextArea rows={3} placeholder={ruTranslationDesc} />
            </Form.Item>
          </Col>
        </Row>
      </Card>
      <Form.List name="questions">
        {(fields, { add, remove }) => (
          <Flex vertical gap={24}>
            {fields?.length > 0 &&
              fields.map(({ key, name }) => (
                <Card key={key}>
                  <Flex vertical gap={16}>
                    <Flex gap={16}>
                      {LANGUAGE_KEYS.map((langKey) => (
                        <Form.Item
                          key={langKey}
                          name={[name, 'text', langKey]}
                          rules={requiredRules}
                          className="full-width"
                        >
                          <Input
                            placeholder={`${langKey.toUpperCase()} ${t('questionnaires.question')}`}
                            suffix={<>{langKey.toUpperCase()}</>}
                          />
                        </Form.Item>
                      ))}
                      <Form.Item
                        name={[name, 'type']}
                        rules={requiredRules}
                        initialValue={QUESTION_TYPES?.[0]?.value}
                      >
                        <Select
                          className={styles.select}
                          options={QUESTION_TYPES}
                          placeholder={t('questionnaires.question_type')}
                        />
                      </Form.Item>

                      <Form.Item
                        name={[name, 'hasOther']}
                        valuePropName="checked"
                        className={styles.switch}
                      >
                        <Checkbox title={t('questionnaires.has_free_text')}>
                          {t('questionnaires.has_free_text')}
                        </Checkbox>
                      </Form.Item>

                      <Popconfirm
                        title={t('questionnaires.delete_question')}
                        onConfirm={() => remove(name)}
                        placement="left"
                      >
                        <Button
                          icon={<DeleteOutlined />}
                          className={styles.deleteButton}
                          danger
                        />
                      </Popconfirm>
                    </Flex>

                    <Form.List name={[name, 'answerChoices']}>
                      {(
                        optionFields,
                        { add: addOption, remove: removeOption }
                      ) => (
                        <>
                          {optionFields.map(
                            ({ key: optionKey, name: optionName }) => {
                              const mediaFile = form.getFieldValue([
                                'questions',
                                name,
                                'answerChoices',
                                optionName,
                                'media'
                              ])

                              const isUploadPending =
                                loadingStates[`${name}-${optionName}`] || false

                              return (
                                <Flex
                                  vertical
                                  gap={8}
                                  key={optionKey}
                                  className={styles.questionCard}
                                >
                                  <Flex align="center" gap={16}>
                                    {LANGUAGE_KEYS.map((langKey) => (
                                      <Form.Item
                                        key={langKey}
                                        name={[optionName, 'text', langKey]}
                                        rules={requiredRules}
                                        className="full-width"
                                      >
                                        <Input
                                          placeholder={t(
                                            'questionnaires.add_option'
                                          )}
                                          suffix={<>{langKey.toUpperCase()}</>}
                                        />
                                      </Form.Item>
                                    ))}

                                    <Upload
                                      customRequest={() => null}
                                      onChange={({ file }) =>
                                        onUpload({
                                          file,
                                          fieldName: name,
                                          optionName
                                        })
                                      }
                                      showUploadList={false}
                                      {...uploadProps}
                                    >
                                      <Button
                                        loading={isUploadPending}
                                        icon={<PaperClipOutlined />}
                                      />
                                    </Upload>

                                    <Popconfirm
                                      title={t('questionnaires.delete_option')}
                                      onConfirm={() => removeOption(optionName)}
                                    >
                                      <Button
                                        icon={<DeleteOutlined />}
                                        className={styles.deleteButton}
                                        danger
                                      />
                                    </Popconfirm>
                                  </Flex>

                                  {(mediaFile?.mediaFile?.uid ||
                                    mediaFile?.mediaFile?.id) && (
                                    <CustomItemRender
                                      isPending={isUploadPending}
                                      file={mediaFile}
                                      onRemove={() => {
                                        const updatedValues =
                                          form.getFieldValue('questions')
                                        updatedValues[name].answerChoices[
                                          optionName
                                        ].media = null

                                        form.setFieldsValue({
                                          questions: updatedValues
                                        })
                                      }}
                                    />
                                  )}
                                </Flex>
                              )
                            }
                          )}
                          <Button
                            onClick={() => onAdd(addOption)}
                            icon={<PlusOutlined />}
                            block
                          >
                            {t('questionnaires.add_option')}
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </Flex>
                </Card>
              ))}
            <Card>
              <Flex justify="center">
                <Form.Item>
                  <Button onClick={() => onAdd(add)} icon={<PlusOutlined />}>
                    {t('questionnaires.add_question')}
                  </Button>
                </Form.Item>
              </Flex>
            </Card>
          </Flex>
        )}
      </Form.List>
    </Flex>
  )
}
